/**
 * PATH: packages/job-hub/src/jobs/qa/qaShortcuts.ts
 * FILE_ID: JOBHUB-QA-SHORTCUTS-001
 * PURPOSE: QA shortcut registry. Combo parsing is the quote sheet's.
 */
import {
  comboFromEvent,
  formatCombo,
  isComboAllowed,
} from '@quotech/quote-builder/lib/workbookShortcuts'

export type QaActionId =
  | 'structure.templates'
  | 'structure.blank'

export interface QaActionDef {
  id: QaActionId
  label: string
  defaultCombo: string
}

export const QA_ACTIONS: QaActionDef[] = [
  { id: 'structure.templates', label: 'Open templates', defaultCombo: 'mod+t' },
  { id: 'structure.blank', label: 'Insert blank template', defaultCombo: 'mod+shift+t' },
]

export const QA_SHORTCUTS_KEY = 'qt.jobhub.qa.shortcuts.v1'

export type QaShortcutOverrides = Partial<Record<QaActionId, string>>

const IDS = new Set(QA_ACTIONS.map((action) => action.id))

export function loadQaShortcutOverrides(): QaShortcutOverrides {
  try {
    const raw = window.localStorage.getItem(QA_SHORTCUTS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const out: QaShortcutOverrides = {}
    for (const [id, combo] of Object.entries(parsed)) {
      if (IDS.has(id as QaActionId) && typeof combo === 'string' && combo) {
        out[id as QaActionId] = combo
      }
    }
    return out
  } catch {
    return {}
  }
}

export function saveQaShortcutOverrides(overrides: QaShortcutOverrides): void {
  try {
    window.localStorage.setItem(QA_SHORTCUTS_KEY, JSON.stringify(overrides))
  } catch {
    /* private mode */
  }
}

export function effectiveQaCombo(overrides: QaShortcutOverrides, id: QaActionId): string {
  return overrides[id] ?? QA_ACTIONS.find((action) => action.id === id)!.defaultCombo
}

export function resolveQaAction(overrides: QaShortcutOverrides, combo: string): QaActionId | null {
  for (const action of QA_ACTIONS) {
    if (effectiveQaCombo(overrides, action.id) === combo) return action.id
  }
  return null
}

export function qaComboFromEvent(event: {
  key: string
  ctrlKey: boolean
  metaKey: boolean
  altKey: boolean
  shiftKey: boolean
}): string | null {
  // Reused from: packages/quote-builder/src/lib/workbookShortcuts.ts (comboFromEvent)
  return comboFromEvent(event)
}

export function qaComboAllowed(combo: string): boolean {
  // Reused from: packages/quote-builder/src/lib/workbookShortcuts.ts (isComboAllowed)
  return isComboAllowed(combo)
}

export function qaFormatCombo(combo: string, isMac: boolean): string {
  // Reused from: packages/quote-builder/src/lib/workbookShortcuts.ts (formatCombo)
  return formatCombo(combo, isMac)
}
